import React, { useEffect, useState } from "react";

// hooks
import useMediaQuery from "@UI/hooks/useMediaQuery";
import { useFeatureFlag } from "@Libs/FeatureFlags/hooks/useFeatureFlag";
import { useLevelUpInfo } from "@modules/ServiceProviders/hooks/useLevelUpInfo";

//I18n
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";

// UI
import {
  Wizard,
  WizardHeader,
  WizardContent,
} from "@Shared/Components/Layouts/Wizard/Wizard";
import { Stack } from "@UI/Components/Stack/Stack";
import { Heading } from "@UI/Components/Heading/Heading";
import { Container } from "@UI/Components/Container/Container";
import { Text } from "@UI/Components/Text/Text";
import { Box } from "@UI/Components/Box/Box";
import { Icon } from "@UI/Components/Icon/Icon";
import { SuperTalentBadge } from "@UI/Components/Icons/SuperTalentBadge";
import { Flex } from "@UI/Components/Flex/Flex";
import { TrackingCard } from "./SuperTalent/TrackingCard";
import { Calendar } from "@UI/Components/Icons/Calendar";
import { MagnifyingGlassChart } from "@UI/Components/Icons/MagnifyingGlassChart";
import { Eye } from "@UI/Components/Icons/Eye";
import { SuperTalentCTA } from "./SuperTalent/SuperTalentCTA";
import { SupertTalentModal } from "./SuperTalent/SuperTalentModal";
import { useNavigate } from "@Libs/Router/hooks/useNavigate";
import { IAppRoute } from "@App/types";
import messages from "./messages";
import { getLocale } from "@I18n/services/i18n";
import { useRoutes } from "@Libs/Router/hooks/useRoutes";
import { Link } from "@UI/Components/Link/Link";

export const SuperTalent = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [nextEvaluationDate, setNextEvaluationDate] = useState("");

  const { isEnabled: isVerifiedSuperTalentEnabled } = useFeatureFlag(
    "verified_super_talent"
  );
  const { levelUpInfo } = useLevelUpInfo();
  const kycInfo = levelUpInfo?.find((info) => info["key"] === "KYC");
  const kycExists = kycInfo !== undefined;
  const isVerified = kycInfo?.state === "DONE";

  const { isMobile } = useMediaQuery();
  const navigate = useNavigate();
  const locale = getLocale();
  const { generateRoute } = useRoutes();

  const handleOpen = () => {
    setIsOpen(true);
  };

  useEffect(() => {
    const calculateNextEvaluationDate = () => {
      const currentDate = new Date();
      const nextMonth = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + 1,
        1
      );
      const formattedDate = nextMonth.toLocaleDateString(locale, {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
      setNextEvaluationDate(formattedDate);
    };

    calculateNextEvaluationDate();
  }, []);

  const handleOnClick = (type: string) => {
    const route = type.toUpperCase();
    switch (type) {
      case "repeat_rate":
        return navigate(["SUPERTALENT_OBJECTIVES", route as IAppRoute]);
      case "realisation_rate":
        return navigate(["SUPERTALENT_OBJECTIVES", route as IAppRoute]);
      default:
        return navigate([route as IAppRoute], {
          reload: true,
        });
    }
  };

  const handleOnClickLeaveReact = (type: string) => {
    const route = type.toUpperCase();
    if (route === "CHART") {
      return generateRoute("CHART_URL");
    }
    return generateRoute([route as IAppRoute]);
  };

  const handleRedirect = () => {
    return generateRoute("SUPER_TALENT_URL");
  };

  return (
    <>
      <Wizard paddingTop={{ base: 5, md: 8 }}>
        <WizardHeader marginBottom={{ base: 5, md: 3 }}>
          <Container
            px={{ base: 0 }}
            maxW={[
              "container.md",
              "container.sm",
              "container.md",
              "container.xl",
            ]}
          >
            <Heading textAlign="start" fontSize={{ base: "3xl", lg: "5xl" }}>
              <FormattedMessage
                {...messages.superTalent}
                values={{
                  superTalentBadge: () => (
                    <Icon
                      as={SuperTalentBadge}
                      boxSize={{ base: "5", lg: "8" }}
                      marginRight="7px"
                      marginBottom="6px"
                      display="inline"
                    />
                  ),
                }}
              />
            </Heading>
          </Container>
        </WizardHeader>
        <WizardContent>
          <Container
            px={{ base: 0 }}
            maxW={[
              "container.md",
              "container.sm",
              "container.md",
              "container.xl",
            ]}
          >
            <Box
              borderBottomWidth="1px"
              borderBottomColor="gray.300"
              pb={{ base: 5, md: 8 }}
            >
              <Text as="span" fontSize={{ base: "md", md: "lg" }}>
                <FormattedMessage {...messages.statusInfos} />
              </Text>
              <SuperTalentCTA onClick={handleOpen} />
            </Box>
            <Flex
              mb={{ base: 2, md: 3 }}
              flexDirection={isMobile ? "column" : "row"}
              pt={{ base: 5, md: 8 }}
            >
              <Heading
                fontSize={{ base: "2xl", lg: "4xl" }}
                lineHeight="none"
              >
                <FormattedMessage {...messages.superTalentGoals} />
              </Heading>
              <Flex
                alignItems="flex-end"
                ml={{ base: 0, md: 3 }}
                mt={{ base: 1, md: 0 }}
              >
                <Text as="span" fontSize="sm">
                  <FormattedMessage
                    {...messages.visibleByYou}
                    values={{
                      eyeIcon: () => (
                        <Icon as={Eye} marginRight="4px" display="inline" />
                      ),
                    }}
                  />
                </Text>
              </Flex>
            </Flex>
            <FormattedMessage {...messages.goalsInfos} />
            <Flex
              flexDirection={{ base: "column", md: "row" }}
              justifyContent="space-between"
              mt={{ base: 5, md: 8 }}
            >
              <Flex
                backgroundColor="sand.200"
                justifyContent="space-around"
                alignItems="center"
                px={3}
                py={1}
                borderRadius="100px"
                w="fit-content"
              >
                <Text
                  color="#666"
                  as="span"
                  fontWeight={400}
                  fontSize={{ base: "sm", md: "md" }}
                >
                  <FormattedMessage
                    {...messages.nextEvaluation}
                    values={{
                      magnifyingGlass: () => (
                        <Icon
                          as={MagnifyingGlassChart}
                          w={4}
                          h={4}
                          mr={1}
                          color="#666"
                          fontWeight={700}
                        />
                      ),
                      nextComputedDate: () => (
                        <Text as="span" ml={1}>
                          {nextEvaluationDate}
                        </Text>
                      ),
                    }}
                  />
                </Text>
              </Flex>
              <Flex
                alignItems="center"
                justifyContent="flex-end"
                alignSelf="stretch"
                mt={{ base: 5, md: 0 }}
              >
                <Text as="span" fontSize={{ base: "sm", md: "md" }}>
                  <FormattedMessage
                    {...messages.dateComputed}
                    values={{
                      calendar: () => (
                        <Icon
                          as={Calendar}
                          marginRight="4px"
                          display="inline"
                        />
                      ),
                    }}
                  />
                </Text>
              </Flex>
            </Flex>
            <Stack
              direction={["column", "row"]}
              mt={{ base: 2, md: 3 }}
              spacing={{ base: 2, md: 3 }}
            >
              <Link
                w="full"
                as="a"
                textDecoration={"none"}
                _hover={{ textDecoration: "none" }}
                href={handleOnClickLeaveReact("average_rating")}
              >
                <TrackingCard type="average_rating" />
              </Link>
              <Link
                w="full"
                as="a"
                textDecoration={"none"}
                _hover={{ textDecoration: "none" }}
                href={handleOnClickLeaveReact("jobs_closed")}
              >
                <TrackingCard type="jobs_closed" />
              </Link>
            </Stack>
            <Stack
              direction={["column", "row"]}
              mt={{ base: 2, md: 3 }}
              spacing={{ base: 2, md: 3 }}
            >
              <TrackingCard type="repeat_rate" onClick={handleOnClick} />
              <TrackingCard type="realisation_rate" onClick={handleOnClick} />
            </Stack>

            <Stack
              direction={["column", "row"]}
              mt={{ base: 2, md: 3 }}
              spacing={{ base: 2, md: 3 }}
            >
              {isVerifiedSuperTalentEnabled && (
                kycExists && !isVerified ? (
                  <Link
                    w="full"
                    as="a"
                    textDecoration={"none"}
                    _hover={{ textDecoration: "none" }}
                    href={handleOnClickLeaveReact("LEGACY_LEVEL_UP")}
                  >
                    <TrackingCard type="verified" />
                  </Link>
                ) : (
                  <Box w="full">
                    <TrackingCard type="verified" />
                  </Box>
                )
              )}

              <Link
                w="full"
                as="a"
                textDecoration={"none"}
                _hover={{ textDecoration: "none" }}
                href={handleOnClickLeaveReact("chart")}
              >
                <TrackingCard type="respects_rules" />
              </Link>
            </Stack>
            <Stack
              direction={["column", "row"]}
              mt={{ base: 2, md: 3 }}
              w={isMobile ? "full" : "50%"}
            ></Stack>
          </Container>
          <SupertTalentModal
            isMobile={isMobile}
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            handleConfirm={handleRedirect}
          />
        </WizardContent>
      </Wizard>
    </>
  );
};
